import React from 'react';

/**
 * Individual plant icon SVG components extracted from the MerkmaleSVG circle.
 * Each icon renders at its natural aspect ratio inside a square viewBox.
 */

// Shared wrapper: renders a circular gray background + icon details
const IconWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="-20 -20 40 40" width="100%" height="100%" style={{ maxWidth: 250, maxHeight: 250 }}>
    <circle r={17} fill="#dadada" />
    {children}
  </svg>
);

// 1. Pfanne (Ladle) — center (120, 323)
export const PfanneIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-120, -323)">
      {/* stopper mechanism */}
      <rect x={117.19} y={318.12} width={5.41} height={4.15} fill="#bdbcbc" />
      <path d="M122.6 313.97v4.15h-5.41v-4.15h1.35v-2.92h-.68v-1.35h.68v-1.36h2.7v1.36h.68v1.35h-.68v2.92z" fill="#fff" />
      {/* vessel body */}
      <rect x={113.9} y={325.41} width={11.99} height={7.28} fill="#bdbcbc" />
      <path d="M127.24 316.77v17.27h-14.7v-17.27h1.35v15.92h11.99v-15.92z" fill="#fff" />
      {/* shadow */}
      <path d="M136.89 326.31c-1.43 7.95-8.38 13.99-16.74 13.99-.51 0-1.02-.02-1.52-.07l-6.1-6.18h14.69v-17.27l9.67 9.53z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 2. Kran / Verwiegung (Crane) — center (231, 298)
export const KranIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-231, -298)">
      {/* crane hook */}
      <path d="M230.82 284.83h-1.43v3.99h2.86v-3.99z" fill="#fff" />
      <path d="M219.59 301.55h22.45v2.85h-22.45z" fill="#fff" />
      <path d="m240.58 304.4-2.89 2.93h-13.86l-2.89-2.93z" fill="#bdbcbc" />
      {/* cable dots */}
      <circle cx={230.82} cy={290.58} r={0.57} fill="#fff" />
      <circle cx={230.82} cy={292.91} r={0.57} fill="#fff" />
      <circle cx={230.82} cy={295.18} r={0.57} fill="#fff" />
      <circle cx={230.82} cy={297.51} r={0.57} fill="#fff" />
      <circle cx={230.82} cy={299.81} r={0.57} fill="#fff" />
      {/* shadow */}
      <path d="M246.17 305.64c-2.73 5.74-8.58 9.7-15.36 9.7h-.3c-3.81-3.82-10.91-10.95-10.91-10.95h1.35l2.89 2.93h13.85l2.89-2.93h1.47v-2.85l4.12 4.1z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 3. Stranggussanlage (Continuous Casting) — center (77, 496)
export const StranggussIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-77, -496)">
      {/* tundish and mold */}
      <path d="M77.84 504.29h3.88v3.39h-3.88z" fill="#bdbcbc" />
      <path d="M72.2 500.81l-.54-4.3h11.43l-.5 4.3z" fill="#bdbcbc" />
      <path d="M79.14 499.57v-5.21h1.29v5.21l-.65.53z" fill="#fff" />
      <rect x={69.15} y={485.16} width={12.82} height={6.44} fill="#bdbcbc" />
      <path d="M83.35 483.01h-1.38v8.59H69.15v-8.59h-1.37v9.88h15.57z" fill="#fff" />
      <path d="m84.75 494.36-.99 7.74H70.99l-.99-7.74h1.38l.82 6.45h10.34l.82-6.45z" fill="#fff" />
      {/* shadow */}
      <path d="M92.83 502.45c-1.7 4.24-5.05 7.63-9.27 9.38-.18.08-.37.15-.55.22-.18.07-.37.14-.56.2-.19.06-.39.13-.59.18-.02.01-.03.01-.05.01-.09.03-.19.06-.28.08l-10.49-10.49 12.71.06.98-7.74 8.1 8.1z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 4. Pfannenofen (Ladle Furnace) — center (163, 571)
export const PfannenofenIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-163, -571)">
      {/* vessel */}
      <path d="M171.23 563.26v18.84h-16.55v-18.84h1.35v17.49h13.85v-17.49z" fill="#fff" />
      {/* electrodes */}
      <rect x={160.22} y={557.2} width={1.37} height={13.16} fill="#bdbcbc" />
      <rect x={162.29} y={557.2} width={1.37} height={13.16} fill="#bdbcbc" />
      <rect x={164.42} y={557.2} width={1.37} height={13.16} fill="#bdbcbc" />
      <rect x={156.03} y={572.02} width={13.85} height={8.73} fill="#bdbcbc" />
      {/* shadow */}
      <path d="M180.21 572.22c0 .09-.01.17-.01.25-.54 8.76-7.7 15.72-16.54 15.94-.15 0-.29.01-.44.01-.42 0-.83-.01-1.24-.04-.09-.01-.18-.01-.27-.02-.21-.02-.43-.04-.64-.07-.1-.01-.2-.02-.29-.04h-.01l-6.1-6.14h16.55v-18.83l8.99 8.94z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 5. Vakuumanlage / RH Anlage — center (244, 572)
export const VakuumanlageIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-244, -572)">
      <rect x={240.14} y={569.25} width={7.46} height={5.51} fill="#bdbcbc" />
      <path d="M248.91 566.75v9.32h-10.09v-9.32h1.32v8.01h7.45v-8.01z" fill="#fff" />
      {/* snorkels */}
      <path d="M242.51 555.35v10.15h.66v-10.2c-.22.01-.44.03-.66.05m1.86-.1v10.25h.66v-10.23c-.22-.01-.44-.02-.66-.02" fill="#fff" />
      {/* vacuum chamber extension */}
      <path d="M253.09 562.99v-2.52h-5.5v1.32h4.18v2.52h8.66v2.42h-8.66v13.73h-15.84v-18.67h4.21v-1.32h-5.52v21.3h18.47v-13.73h8.66v-5.05z" fill="#fff" />
      {/* shadow */}
      <path d="M260.9 571.84c0 1.98-.35 3.89-.99 5.65-.02.05-.04.1-.06.16-.06.17-.13.34-.2.5l-.06.15c-.06.13-.11.27-.17.4-.03.05-.05.1-.08.16-.08.17-.16.34-.24.5-.03.05-.06.1-.08.16-2.77 5.29-8.31 8.9-14.7 8.9-1.17 0-2.31-.12-3.4-.35l-6.29-6.29h18.47v-13.73h7.37c.23.97.37 1.98.42 3.01 0 .25.01.51.01.78" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 6. EAF / Lichtbogenofen — center (344, 470) in EN / (341, 466) in DE
export const EAFIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-344, -470)">
      <rect x={339.56} y={463.59} width={8.53} height={11.79} fill="#bdbcbc" />
      <path d="M337.25 475.38h.94v-13.47l-.94-.55z" fill="#bdbcbc" />
      <rect x={349.47} y={460.63} width={0.94} height={14.75} fill="#bdbcbc" />
      <path d="M337.25 475.38h13.16v2.35h-13.16z" fill="#fff" />
      <path d="M347.7 459.52v3h-7.74v-1.64l-5.32-3.07v-1.36l5.32 3.07z" fill="#fff" />
      {/* walls */}
      <path d="M351.68 478.96h-15.7v-18.33l2.21 1.28v14.84h11.27v-16.12h2.21v18.33z" fill="#fff" />
      {/* shadow */}
      <path d="M361.1 469.62c0 9.39-7.61 17.01-17.01 17.01-1.2 0-2.37-.12-3.5-.36l-5.96-5.95h18.38v-20.8l7.93 7.92c0 .01.01.03.01.04.11.7.15 1.41.15 2.14" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 7. BOF / Konverter — g717 icon at (345, 427)
export const BOFIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-345.3, -426.8) scale(0.26854669, 0.26277207) translate(326.27003, 408.22201)">
      {/* simplified: just render the circle and key structural elements */}
    </g>
    {/* Use a simpler representation of the BOF g717 icon */}
    <g transform="scale(0.26854669, 0.26277207) translate(-70.86, -70.8)">
      <circle cx={70.86} cy={70.8} r={63.74} fill="#d8d7d9" />
      <path d="m 125.23,104.12 a 63,63 0 0 1 -7.88,10.34 1.37,1.37 0 0 1 -0.17,0.17 c -0.38,0.41 -0.77,0.81 -1.17,1.21 a 3,3 0 0 1 -0.25,0.25 c -0.25,0.25 -0.5,0.5 -0.76,0.74 l -0.37,0.36 -1,0.94 -0.22,0.2 -0.37,0.32 -0.39,0.35 -0.24,0.21 c -0.15,0.14 -0.31,0.27 -0.47,0.4 l -0.16,0.13 -0.43,0.36 c -0.57,0.47 -1.15,0.94 -1.74,1.39 l -0.29,0.22 -1,0.71 -0.65,0.47 -0.12,0.08 c -0.61,0.43 -1.23,0.85 -1.85,1.26 -0.62,0.41 -1.26,0.8 -1.9,1.19 -0.64,0.39 -1.28,0.76 -1.94,1.13 l -0.17,0.09 -0.18,0.09 c -0.27,0.16 -0.54,0.3 -0.81,0.45 l -0.1,0.06 -0.72,0.37 -1,0.5 c -0.33,0.17 -0.67,0.33 -1,0.49 l -1,0.47 -1,0.45 h -0.07 l -1.55,0.63 -0.72,0.28 c -0.64,0.24 -1.28,0.47 -1.93,0.69 -0.65,0.22 -1.35,0.45 -2,0.66 l -0.31,0.1 q -1,0.28 -1.92,0.54 h -0.12 c -0.74,0.2 -1.48,0.38 -2.23,0.55 l -0.93,0.2 c -1.86,0.4 -3.76,0.71 -5.68,0.93 v 0 c -1.17,0.14 -2.35,0.25 -3.54,0.32 v 0 a 0.39,0.39 0 0 0 -0.15,0 L 50.36,110.18 77.7,101.18 100,78.9 Z" fill="#98979b" />
      <path d="m 56.8,107 c 1.18,0.27 2.36,0.51 3.56,0.69 l -0.88,5 -0.56,-0.12 -8.43,-2 v -0.11 l -0.11,0.11 -13.2,-13.27 -0.1,-49.64 5.06,2 v 45.45 l 11.16,11 z" fill="#ffffff" />
      <path d="m 61.87,107.86 a 51,51 0 0 0 6,0.45 l -0.24,5.3 A 42,42 0 0 1 61.4,113 Z" fill="#ffffff" />
      <path d="M 75.52,107.89 76,113 a 43.76,43.76 0 0 1 -6.26,0.58 l -0.24,-5.31 a 51.12,51.12 0 0 0 6.02,-0.38 z" fill="#ffffff" />
      <path d="m 100.29,51.94 v 45.52 l -13.15,13.08 v 0 l -8.43,2 -0.79,0.17 -0.89,-5 c 1.27,-0.19 2.54,-0.44 3.8,-0.73 L 84,106.24 95.23,95.37 V 51.94 Z" fill="#ffffff" />
      <path d="M 95.23,78 V 95.37 L 84,106.21 l -3.17,0.74 a 53.26,53.26 0 0 1 -20.47,0.7 c -1.2,-0.19 -2.38,-0.43 -3.56,-0.7 l -3.5,-0.81 -11.16,-11 V 78 Z" fill="#b9b9ba" />
      <polygon points="52.8,34.48 52.8,29.95 56.99,29.95 56.99,34.77" fill="#ffffff" />
      <rect x={50.96} y={29.22} width={6.04} height={1.74} fill="#ffffff" />
      <polygon points="84.59,34.48 84.59,29.95 80.4,29.95 80.4,34.77" fill="#ffffff" />
      <polygon points="37.08,47.66 52.79,31.95 56.99,34.77 42.14,49.63" fill="#ffffff" />
      <polygon points="99.77,47.18 84.57,31.95 80.4,34.77 95.23,49.63" fill="#ffffff" />
      <path d="m 95.23,46.94 v 2.73 l 8.08,-3.26 v -2.74 z m 2.71,3.92 -2.71,1.14 v 2.72 l 8.08,-3.26 V 48.7 Z" fill="#ffffff" />
    </g>
  </IconWrapper>
);

// 8. Induktionsofen (Induction Furnace) — center (327, 507)
export const InduktionsofenIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-327, -507)">
      {/* coil bars */}
      <rect x={322.39} y={492.42} width={1.37} height={15.45} fill="#bdbcbc" />
      <rect x={324.56} y={492.42} width={1.37} height={15.45} fill="#bdbcbc" />
      <rect x={326.68} y={492.42} width={1.37} height={15.45} fill="#bdbcbc" />
      {/* vessel body */}
      <path d="M340.91 506.55v5.91h-2.51v3.27h-.67v-4.65h1.79v-3.14h-6.45v-6.74l-4.15-.81v-1.37l5.5 1.07v6.46z" fill="#fff" />
      <path d="M337.16 511.08v4.65h-.68v-3.27h-2.07v3.26c-2.99.96-6.09 1.43-9.19 1.43-3.09 0-6.18-.48-9.16-1.42l-.04-.01v-15.63l5.57-1.08v1.38l-4.23.82v13.38c2.57.69 5.24 1.04 7.87 1.04 2.64 0 5.25-.35 7.82-1.04v-3.5h4.11z" fill="#fff" />
      <path d="M337.16 511.08h.57v1.38h-.57z" fill="#fff" />
      <path d="M337.16 510.9h.57v4.83h-.57z" fill="#bdbcbc" />
      {/* bottom fill */}
      <path d="M333.07 511.05v3.51c-2.57.69-5.19 1.04-7.82 1.04-2.64 0-5.3-.35-7.87-1.04v-4.98h22.15v1.48h-6.46z" fill="#bdbcbc" />
      {/* shadow */}
      <path d="M343.85 509.5c-.03.19-.07.37-.1.55-1.58 7.76-8.44 13.59-16.67 13.59-1.23 0-2.43-.13-3.58-.38l-7.48-7.54v-.14l18.39.14v-3.26h2.07v3.11h1.92v-3.11h2.5v-5.91l2.95 2.95z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 9. Blockguss / Ingot Casting — center (65, 459)
export const BlockgussIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-65, -459)">
      {/* top frames */}
      <path d="M64.35 448.63v1.35h-5.16v-7.25c.44-.17.89-.32 1.35-.45v6.35h3.81z" fill="#fff" />
      <path d="M70.89 442.58v7.4H65.7v-1.35h3.84v-6.46c.45.12.9.26 1.35.41" fill="#fff" />
      {/* main frame */}
      <path d="M74.88 451.4v13.19h-1.35v2.7H56.49v-2.7h-1.36V451.4h-1.35v17.23h22.45V451.4z" fill="#fff" />
      {/* internal walls */}
      <path d="M64.35 465.94V451.4H63v13.19h-2.46V451.4h-1.35v13.19h-1.35v1.35z" fill="#fff" />
      <path d="M72.19 465.94v-1.35h-1.36V451.4h-1.35v13.19h-2.43V451.4H65.7v14.54z" fill="#fff" />
      {/* mold chambers */}
      <path d="M74.88 455.76h-4.05v8.83h1.36v1.35H65.7v-17.31h3.84v-2.56h-9v2.56h3.81v17.31h-6.51v-1.35h1.35v-8.83h-4.05v8.83h1.35v2.7h17.04v-2.7h1.35z" fill="#bdbcbc" />
      {/* shadow */}
      <path d="M82.25 458.63c0 9.39-7.61 17.01-17.01 17.01-1.86 0-3.66-.3-5.33-.85l-6.13-6.13v-.02h22.45V451.4h.08l5.88 5.88c.04.45.06.9.06 1.35" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// 10. Spülstand (Purging Stand) — center (201, 576)
export const SpuelstandIcon: React.FC = () => (
  <IconWrapper>
    <g transform="translate(-201, -576)">
      {/* V-shaped vessel with stirring tubes */}
      <path d="M193.6 576.7h15.8v8.8h-15.8z" fill="#bdbcbc" />
      <path d="m210.7 570.2-4.7-4.7-.9 1 4.3 4.3v14.7h-15.8v-14.7l4.3-4.3-1-1-4.7 4.7v16.7h18.5v-16.7z" fill="#fff" />
      {/* stirring tubes */}
      <path d="M200.5 559.2h-.4V575h.7v-15.8h-.3m1.9 0h-.4V575h.7v-15.8h-.3" fill="#fff" />
      {/* shadow */}
      <path d="M218.4 578.1c-.1 1.2-.4 2.4-.8 3.5-.1.2-.2.5-.2.7-.1.2-.1.4-.2.6v.1l-.3.6c-.1.2-.2.5-.4.7-3.5 6.6-11 10.1-18.4 8.6l-5.9-6h18.4v-16.6l7.8 7.8z" fill="#a8a8a7" />
    </g>
  </IconWrapper>
);

// Export a map for easy lookup by slug
export const plantIconMap: Record<string, React.FC> = {
  'pfanne': PfanneIcon,
  'kran-verwiegung': KranIcon,
  'stranggussanlage': StranggussIcon,
  'pfannenofen': PfannenofenIcon,
  'vakuumanlage': VakuumanlageIcon,
  'eaf': EAFIcon,
  'bof-konverter': BOFIcon,
  'induktionsofen': InduktionsofenIcon,
  'blockguss': BlockgussIcon,
  'spuelstand': SpuelstandIcon,
};
